import { useCallback, useEffect, useRef } from 'react';
import { ImagePerformanceMonitor } from '../utils/imageOptimization';

interface ImagePerformanceMetrics {
  loadTime?: number;
  failed: boolean;
  retryCount: number;
}

interface UseImagePerformanceOptions {
  onLoad?: (metrics: ImagePerformanceMetrics) => void;
  onError?: (metrics: ImagePerformanceMetrics) => void;
  maxRetries?: number;
  retryDelay?: number;
}

/**
 * Hook for monitoring image loading performance and handling failures
 */
export const useImagePerformance = (src: string, options: UseImagePerformanceOptions = {}) => {
  const { onLoad, onError, maxRetries = 2, retryDelay = 1000 } = options;

  const monitor = ImagePerformanceMonitor.getInstance();
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<number | null>(null);

  const handleLoadStart = useCallback(() => {
    monitor.recordLoadStart(src);
  }, [src, monitor]);

  const handleLoadSuccess = useCallback(() => {
    monitor.recordLoadEnd(src);
    const metrics: ImagePerformanceMetrics = {
      failed: false,
      retryCount: retryCountRef.current,
    };
    onLoad?.(metrics);
  }, [src, monitor, onLoad]);

  const handleLoadError = useCallback(() => {
    monitor.recordLoadError(src);
    retryCountRef.current++;

    const metrics: ImagePerformanceMetrics = {
      failed: true,
      retryCount: retryCountRef.current,
    };

    // Attempt retry if under max retries
    if (retryCountRef.current <= maxRetries) {
      retryTimeoutRef.current = window.setTimeout(() => {
        // Trigger a retry by updating the image src with a cache-busting parameter
        const retryEvent = new CustomEvent('image-retry', {
          detail: { src, retryCount: retryCountRef.current },
        });
        window.dispatchEvent(retryEvent);
      }, retryDelay);
    } else {
      onError?.(metrics);
    }
  }, [src, monitor, onError, maxRetries, retryDelay]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        window.clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return {
    handleLoadStart,
    handleLoadSuccess,
    handleLoadError,
    retryCount: retryCountRef.current,
    isRetrying: retryTimeoutRef.current !== null,
  };
};

/**
 * Hook for tracking overall image performance across the app
 */
export const useGlobalImagePerformance = () => {
  const monitor = ImagePerformanceMonitor.getInstance();

  const getFailedImages = useCallback(() => {
    return monitor.getFailedLoads();
  }, [monitor]);

  const clearMetrics = useCallback(() => {
    monitor.clearMetrics();
  }, [monitor]);

  const getPerformanceReport = useCallback(() => {
    const failedImages = monitor.getFailedLoads();

    return {
      totalFailedImages: failedImages.length,
      failedImages,
      recommendations: generatePerformanceRecommendations(failedImages),
    };
  }, [monitor]);

  return {
    getFailedImages,
    clearMetrics,
    getPerformanceReport,
  };
};

/**
 * Generate performance recommendations based on failed images
 */
function generatePerformanceRecommendations(failedImages: string[]): string[] {
  const recommendations: string[] = [];

  if (failedImages.length > 0) {
    recommendations.push('Some images failed to load. Consider implementing fallback images.');
  }

  if (failedImages.length > 3) {
    recommendations.push(
      'Multiple image failures detected. Check network connectivity and image URLs.',
    );
  }

  const pngImages = failedImages.filter((img) => img.includes('.png'));
  if (pngImages.length > 0) {
    recommendations.push('Consider converting PNG images to WebP format for better performance.');
  }

  const largeImages = failedImages.filter(
    (img) =>
      img.includes('video_version') || img.includes('meet-image') || img.includes('product-mockup'),
  );
  if (largeImages.length > 0) {
    recommendations.push('Large images detected. Implement lazy loading and image compression.');
  }

  return recommendations;
}
